import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EnrollmentView } from 'components/enrollments/EnrollmentView';
import { FormViewer } from 'components/forms/FormViewer';
import type { EnrollmentResponseDto } from 'models/api/EnrollmentResponseDto';
import type { Form } from 'models/forms/Form';
import { AppointmentEnrollment } from 'models/enrollments/AppointmentEnrollment';
import { ConsultationEnrollment } from 'models/enrollments/ConsultationEnrollment';
import { EventEnrollment } from 'models/enrollments/EventEnrollment';
import { FormEnrollment } from 'models/enrollments/FormEnrollment';
import { InvoiceEnrollment } from 'models/enrollments/InvoiceEnrollment';
import { SecurityDepositEnrollment } from 'models/enrollments/SecurityDepositEnrollment';
import type { FormSubmission } from 'models/submissions/FormSubmission';
import type { ConsultationSubmission } from 'models/submissions/ConsultationSubmission';
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import { NodeInstanceStatus } from 'models/nodes/NodeInstanceStatus';
import { NodeType } from 'models/nodes/NodeType';
import type {
  UserWorkflowInstanceDetailDto,
  UserWorkflowNodeDto,
} from 'models/workflows/UserWorkflowInstanceDetailDto';
import { parseConsultationOutput } from 'models/appointments';
import { ApiService } from 'services/ApiService';
import { utilities } from 'utils/utilities';
import AppointmentActionsPanel from './appointments/AppointmentActionsPanel';

type Props = {
  detail: UserWorkflowInstanceDetailDto;
  isSubmitting: boolean;
  onSubmittingChange: (isSubmitting: boolean) => void;
  onSubmitted: () => Promise<void> | void;
};

const STATUS_BADGE: Record<NodeInstanceStatus, string> = {
  [NodeInstanceStatus.Pending]: 'bg-gray-100 text-gray-600 border border-gray-300',
  [NodeInstanceStatus.Active]: 'bg-blue-100 text-blue-700 border border-blue-400',
  [NodeInstanceStatus.Waiting]: 'bg-yellow-100 text-yellow-700 border border-yellow-400',
  [NodeInstanceStatus.Completed]: 'bg-green-100 text-green-700 border border-green-400',
  [NodeInstanceStatus.Failed]: 'bg-red-100 text-red-700 border border-red-400',
};

const NODE_LABEL: Partial<Record<NodeType, string>> = {
  [NodeType.Form]: 'Form',
  [NodeType.TeamReview]: 'Tattoo Team Review',
  [NodeType.AutomatedAction]: 'Automated Step',
  [NodeType.Consultation]: 'Consultation',
  [NodeType.SecurityDeposit]: 'Security Deposit',
  [NodeType.Invoice]: 'Invoice',
};

const isCurrent = (node: UserWorkflowNodeDto): boolean =>
  node.status === NodeInstanceStatus.Active || node.status === NodeInstanceStatus.Waiting;

const formatDateTime = (iso?: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

const formatCurrency = (minorUnits: number, currency: string): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(
    minorUnits / 100,
  );

function parseJson<T>(raw?: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function submittedFormFromOutput(outputJson?: string | null): Form | null {
  const parsed = parseJson<Record<string, unknown>>(outputJson);
  if (!parsed) return null;

  try {
    const source: Record<string, unknown> =
      typeof parsed.responseJson === 'string'
        ? (JSON.parse(parsed.responseJson) as Record<string, unknown>)
        : parsed;

    const rawSubmission = source.userSubmission as Record<string, unknown> | undefined;
    const form = rawSubmission?.form as Form | undefined;
    return form?.id && Array.isArray(form.elements) ? form : null;
  } catch {
    return null;
  }
}

function enrollmentFromDto(dto: EnrollmentResponseDto) {
  const base = {
    id: dto.enrollmentId,
    nodeInstanceId: dto.nodeInstanceId,
    requiredSubmissionType: dto.requiredSubmissionType,
  };

  switch (dto.enrollmentType) {
    case 'FormEnrollment':
      return FormEnrollment.fromDto({ ...base, formId: dto.formId });
    case 'ConsultationEnrollment':
      return ConsultationEnrollment.fromDto({
        ...base,
        consultationType: dto.consultationType,
        consultationAddress: dto.consultationAddress,
        videoProvider: dto.videoProvider,
      });
    case 'EventEnrollment':
      return EventEnrollment.fromDto({ ...base, eventName: dto.eventName });
    case 'SecurityDepositEnrollment':
      return SecurityDepositEnrollment.fromDto({
        ...base,
        amountMinorUnits: dto.amountMinorUnits,
        currency: dto.currency,
        localPaymentStatus: dto.localPaymentStatus,
        description: dto.description,
        isAwaitingTeamInput: dto.isAwaitingTeamInput,
      });
    case 'InvoiceEnrollment':
      return InvoiceEnrollment.fromDto({
        ...base,
        totalMinorUnits: dto.totalMinorUnits,
        depositCreditMinorUnits: dto.depositCreditMinorUnits,
        currency: dto.currency,
        autoCharge: dto.autoCharge,
        localInvoiceStatus: dto.localInvoiceStatus,
        hostedInvoiceUrl: dto.hostedInvoiceUrl,
        description: dto.description,
        isAwaitingTeamInput: dto.isAwaitingTeamInput,
      });
    case 'AppointmentEnrollment':
      return AppointmentEnrollment.fromDto({
        ...base,
        appointmentTimeUtc: dto.appointmentTimeUtc,
        location: dto.location,
        durationMinutes: dto.durationMinutes,
        suggestedTimesJson: dto.suggestedTimesJson,
        attendanceOutcome: dto.attendanceOutcome,
      });
    default:
      return FormEnrollment.fromDto({ ...base, formId: dto.formId });
  }
}

const CompletedOutput: React.FC<{
  node: UserWorkflowNodeDto;
  teamId: string;
  /** Awaited after a customer appointment mutation so the refetched detail
   *  (and its outputJson) stays the display source of truth. */
  onAppointmentChanged?: () => Promise<void> | void;
}> = ({ node, teamId, onAppointmentChanged }) => {
  if (!node.outputJson) {
    return <p className="text-sm text-gray-500">No submitted output was recorded for this step.</p>;
  }

  if (node.nodeType === NodeType.Form) {
    const form = submittedFormFromOutput(node.outputJson);
    if (form) {
      return (
        <FormViewer
          formId={node.formId ?? form.id}
          form={form}
          isDisabled={true}
          teamId={teamId}
        />
      );
    }
  }

  if (node.nodeType === NodeType.TeamReview) {
    const review = parseJson<{ Decision?: string; decision?: string }>(node.outputJson);
    const decision = review?.Decision ?? review?.decision ?? 'Completed';
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
        Tattoo team review completed: <span className="font-medium">{decision}</span>
      </div>
    );
  }

  if (node.nodeType === NodeType.Consultation) {
    const appointment = parseConsultationOutput(node.outputJson);
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
        <p className="font-medium">Consultation confirmed</p>
        {appointment?.appointmentTimeUtc && (
          <p>Time: {formatDateTime(appointment.appointmentTimeUtc)}</p>
        )}
        {appointment?.address && <p>Location: {appointment.address}</p>}
        {appointment?.meetingUrl && <p>Meeting link: {appointment.meetingUrl}</p>}
        {appointment && onAppointmentChanged && (
          <AppointmentActionsPanel
            appointment={appointment}
            scope="customer"
            onRefresh={onAppointmentChanged}
          />
        )}
      </div>
    );
  }

  if (node.nodeType === NodeType.SecurityDeposit) {
    const out = parseJson<{ AmountMinorUnits?: number; Currency?: string }>(node.outputJson);
    if (out?.AmountMinorUnits && out.Currency) {
      return (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          Deposit received: {formatCurrency(out.AmountMinorUnits, out.Currency)}
        </div>
      );
    }
  }

  if (node.nodeType === NodeType.Invoice) {
    const out = parseJson<{ AmountPaidMinorUnits?: number; Currency?: string; DepositCreditMinorUnits?: number }>(node.outputJson);
    if (out?.AmountPaidMinorUnits && out.Currency) {
      return (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 space-y-1">
          {(out.DepositCreditMinorUnits ?? 0) > 0 && (
            <p>Deposit credit applied: -{formatCurrency(out.DepositCreditMinorUnits!, out.Currency)}</p>
          )}
          <p>Invoice paid: {formatCurrency(out.AmountPaidMinorUnits, out.Currency)}</p>
        </div>
      );
    }
  }

  const parsed = parseJson<unknown>(node.outputJson);
  return (
    <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono rounded-lg border border-gray-200 bg-gray-50 p-3">
      {parsed ? JSON.stringify(parsed, null, 2) : node.outputJson}
    </pre>
  );
};

export const UserWorkflowInstanceView: React.FC<Props> = ({
  detail,
  isSubmitting,
  onSubmittingChange,
  onSubmitted,
}) => {
  const currentRef = useRef<HTMLDivElement>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sortedNodes = useMemo(
    () => [...detail.nodes].sort((a, b) => a.order - b.order),
    [detail.nodes],
  );

  useEffect(() => {
    currentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setSubmitError(null);
  }, [detail.id]);

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submitToEnrollment = async (enrollmentId: string, submission: FormSubmission | ConsultationSubmission) => {
    onSubmittingChange(true);
    setSubmitError(null);
    try {
      await ApiService.submitToEnrollment(enrollmentId, submission);
      await onSubmitted();
    } catch (err) {
      console.error('Failed to submit workflow enrollment:', err);
      setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      onSubmittingChange(false);
    }
  };

  const refreshStatus = async () => {
    setSubmitError(null);
    try {
      await onSubmitted();
    } catch (err) {
      console.error('Failed to refresh workflow status:', err);
      setSubmitError(err instanceof Error ? err.message : 'Unable to refresh this project. Please try again.');
    }
  };

  const handleFormSubmit = (node: UserWorkflowNodeDto, submissionDto: UserSubmissionDto) => {
    if (!node.activeEnrollment) return;
    const submission: FormSubmission = {
      id: utilities.DEFAULT_GUID,
      enrollmentId: node.activeEnrollment.enrollmentId,
      nodeInstanceId: node.id,
      workflowInstanceId: detail.id,
      submittedByUserId: '',
      type: 'FormSubmission',
      submittedAt: new Date().toISOString(),
      responseJson: JSON.stringify(submissionDto),
    };
    void submitToEnrollment(node.activeEnrollment.enrollmentId, submission);
  };

  const handleConsultationSubmit = (node: UserWorkflowNodeDto, submission: ConsultationSubmission) => {
    if (!node.activeEnrollment) return;
    void submitToEnrollment(node.activeEnrollment.enrollmentId, {
      ...submission,
      id: utilities.DEFAULT_GUID,
      enrollmentId: node.activeEnrollment.enrollmentId,
      nodeInstanceId: node.id,
      workflowInstanceId: detail.id,
      submittedByUserId: '',
      submittedAt: new Date().toISOString(),
    });
  };

  const handleSuggestAppointmentTimes = async (enrollmentId: string, times: string[]) => {
    setSubmitError(null);
    onSubmittingChange(true);
    try {
      await ApiService.suggestAppointmentTimes(enrollmentId, times);
      await onSubmitted();
    } catch (err) {
      console.error('Failed to suggest appointment times:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to suggest times. Please try again.');
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <div className="px-5 py-4">
      {submitError && (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {submitError}
        </div>
      )}

      {sortedNodes.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          This project has not reached a visible step yet.
        </div>
      ) : (
        <ol className="relative space-y-0" aria-label="Project workflow steps">
          {sortedNodes.map((node, index) => {
            const expanded = expandedIds.has(node.id);
            const completed = node.status === NodeInstanceStatus.Completed;
            const current = isCurrent(node);
            const label = NODE_LABEL[node.nodeType] ?? `Step ${node.order}`;
            const isLast = index === sortedNodes.length - 1;
            const enrollment = node.activeEnrollment ? enrollmentFromDto(node.activeEnrollment) : null;

            return (
              <li key={node.id} className="relative flex gap-4">
                {!isLast && (
                  <div className="absolute left-[19px] top-8 bottom-0 w-px bg-gray-200" aria-hidden="true" />
                )}

                <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center">
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ring-2 ${
                      completed
                        ? 'bg-green-500 ring-green-300'
                        : current
                        ? 'bg-blue-500 ring-blue-300'
                        : node.status === NodeInstanceStatus.Failed
                        ? 'bg-red-500 ring-red-300'
                        : 'bg-gray-300 ring-gray-200'
                    }`}
                    aria-hidden="true"
                  />
                </div>

                <div ref={current ? currentRef : undefined} className="flex-1 pb-6">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Step {node.order} - {label}
                      </p>
                      {node.completedAt && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Completed {formatDateTime(node.completedAt)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[node.status]}`}>
                        {node.status}
                      </span>
                      {completed && node.outputJson && (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(node.id)}
                          className="text-xs text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                          aria-expanded={expanded}
                        >
                          {expanded ? 'Hide submitted info' : 'View submitted info'}
                        </button>
                      )}
                    </div>
                  </div>

                  {completed && expanded && (
                    <div className="mt-3">
                      <CompletedOutput
                        node={node}
                        teamId={detail.teamId}
                        onAppointmentChanged={refreshStatus}
                      />
                    </div>
                  )}

                  {node.blockedReason && (
                    <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      {node.blockedReason}
                    </div>
                  )}

                  {enrollment && (
                    <div className="mt-3">
                      {enrollment instanceof FormEnrollment ? (
                        node.activeForm ? (
                          <FormViewer
                            formId={node.formId ?? node.activeForm.id}
                            form={node.activeForm}
                            isDisabled={isSubmitting}
                            workflowId={detail.id}
                            nodeId={node.id}
                            teamId={detail.teamId}
                            onSubmit={(dto) => handleFormSubmit(node, dto)}
                          />
                        ) : (
                          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            This form is not available yet. Please refresh and try again.
                          </div>
                        )
                      ) : (
                        <EnrollmentView
                          enrollment={enrollment}
                          teamId={detail.teamId}
                          workflowId={detail.id}
                          nodeId={node.id}
                          isDisabled={isSubmitting}
                          onSubmit={(dto) => handleFormSubmit(node, dto)}
                          onSubmitConsultation={(submission) => handleConsultationSubmit(node, submission)}
                          onStatusRefresh={() => { void refreshStatus(); }}
                          onSuggestTimes={enrollment instanceof AppointmentEnrollment
                            ? (times) => handleSuggestAppointmentTimes(enrollment.id, times)
                            : undefined}
                        />
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default UserWorkflowInstanceView;
